import { cn } from "@tellescope/ui";
import { CommonCalendarOptions, DateRange } from "../../types";
import { CalendarFilters } from "../../filters";
import { isWithinInterval } from "date-fns/isWithinInterval";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import { getZonedDayStart } from "../../utils";
import { CurrentTimeLine } from "../CurrentTimeLine";
import { TimeColumn, TimeColumnHeader } from "../TimeColumn";
import { WeekViewHeaders } from "./WeekViewHeaders";
import { WeekViewHourGrid } from "./WeekViewHourGrid";
import { WeekViewMultiDayRow } from "./WeekViewMultiDayRow";
import { useCalendarWeekViewLogic } from "./useCalendarWeekViewLogic";
import { DAY_COLUMN, WEEK_VIEW } from "../constants";
import { EventCreateDialog, UnavailableUsersWarningDialog } from "../dialogs";
import { DndContext } from "@dnd-kit/core";
import { useDayColumnDragLogic } from "../CalendarDayView";
import { useTranslation } from "react-i18next";
import { HiddenEventsExpander } from "../HiddenEventsExpander";

export interface CalendarWeekViewProps extends DateRange, CommonCalendarOptions {
  timezone: string;
  referenceDate: Date;
  className?: string;
  getOnDayClick?: (date: Date) => (() => void) | string;
  filters?: CalendarFilters;
  currentUserId?: string;
  showAvailability?: boolean;
}

export const CalendarWeekView = ({
  startDate,
  endDate,
  referenceDate,
  filters,
  timezone,
  className,
  getOnDayClick,
  showAvailability,
  currentUserId,
}: CalendarWeekViewProps) => {
  const { t } = useTranslation("calendar");
  const {
    browserTimezone,
    showTwoTimezones,
    daysOfWeek,
    multiDayEvents,
    multiDayEventsByDate,
    scrollContainerRef,
    thumbRef,
    handleScroll,
    headersRef,
    normalEventsByDate,
    creationMetadata,
    handleCreateClick,
    selectedUserIds,
    closeCreation,
    dayStartMinutes,
    dayEndMinutes,
    hasEarlierHiddenEvents,
    hasLaterHiddenEvents,
    handleExpandEarlier,
    handleExpandLater,
    isNearTop,
    isNearBottom,
  } = useCalendarWeekViewLogic({
    startDate,
    endDate,
    referenceDate,
    timezone,
    className,
    getOnDayClick,
    filters,
    currentUserId,
  });

  const {
    sensors,
    activeDrag,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
    updateWarningDialogProps,
  } = useDayColumnDragLogic({ startDate, timezone });

  return (
    <section
      data-testid="calendar-view-week"
      className={cn("@container flex flex-col min-h-0 overflow-hidden flex-grow", className)}
    >
      <div className="@[500px]:hidden flex items-start justify-center p-4 text-center text-sm text-muted-foreground border-b">
        <div className="space-y-1 font-light">
          <p>{t("Weekly view is not available on smaller devices.")}</p>
          <p>{t("Please switch to daily or monthly view.")}</p>
        </div>
      </div>
      <div className="hidden @[500px]:block relative flex-grow min-h-0 overflow-hidden">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="relative h-full w-full overflow-y-auto [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
        >
          <div ref={headersRef} className="sticky top-0 z-[1000] bg-opacity-70 bg-white backdrop-blur-sm flex flex-col">
            {!!multiDayEvents.length && (
              <div
                className="grid divide-x bg-opacity-0"
                style={{ gridTemplateColumns: WEEK_VIEW.GRID_TEMPLATE_COLUMNS }}
              >
                <WeekViewMultiDayRow
                  daysOfWeek={daysOfWeek}
                  startDate={startDate}
                  multiDayEventsByDate={multiDayEventsByDate}
                  timezone={timezone}
                />
              </div>
            )}
            <div
              className="grid divide-x bg-opacity-0"
              style={{ gridTemplateColumns: WEEK_VIEW.GRID_TEMPLATE_COLUMNS }}
            >
              <TimeColumnHeader
                startDate={startDate}
                timezone={timezone}
                browserTimezone={browserTimezone}
                showTwoTimezones={showTwoTimezones}
              />
              <WeekViewHeaders startDate={startDate} timezone={timezone} getOnDayClick={getOnDayClick} />
            </div>
            {hasEarlierHiddenEvents && (
              <HiddenEventsExpander
                direction="earlier"
                onClick={handleExpandEarlier}
                visible={isNearTop}
                className="-bottom-8 hover:opacity-100"
              />
            )}
          </div>
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className="relative grid divide-x" style={{ gridTemplateColumns: WEEK_VIEW.GRID_TEMPLATE_COLUMNS }}>
              <TimeColumn
                startDate={startDate}
                timezone={timezone}
                browserTimezone={browserTimezone}
                showTwoTimezones={showTwoTimezones}
                dayStartMinutes={dayStartMinutes}
                dayEndMinutes={dayEndMinutes}
              />
              <WeekViewHourGrid
                startDate={startDate}
                daysOfWeek={daysOfWeek}
                eventsByDate={normalEventsByDate}
                onTimeClick={handleCreateClick}
                onCreateRange={handleCreateClick}
                activeDrag={activeDrag}
                timezone={timezone}
                dayStartMinutes={dayStartMinutes}
                dayEndMinutes={dayEndMinutes}
                userIds={filters?.userIds}
                showAvailability={showAvailability}
              />
              <CurrentTimeLine
                shouldDisplay={(date) => {
                  if (!isWithinInterval(date, { start: startDate, end: endDate })) return false;
                  const minutesIntoDay = differenceInMinutes(date, getZonedDayStart(date, timezone));
                  return minutesIntoDay >= dayStartMinutes && minutesIntoDay <= dayEndMinutes;
                }}
                timezone={timezone}
                getTop={(date) =>
                  (differenceInMinutes(date, getZonedDayStart(date, timezone)) - dayStartMinutes) *
                  DAY_COLUMN.MINUTE_HEIGHT
                }
                textAvailableWidth={DAY_COLUMN.LEFT_COLUMN_WIDTH - 7}
              />
            </div>
          </DndContext>
        </div>
        <div
          ref={thumbRef}
          className="absolute top-0 right-1 w-1.5 bg-black/40 rounded-full pointer-events-none opacity-0 transition-opacity duration-300 z-[10000]"
        />
        {hasLaterHiddenEvents && (
          <HiddenEventsExpander
            direction="later"
            onClick={handleExpandLater}
            visible={isNearBottom}
            className="bottom-2"
          />
        )}
      </div>
      <EventCreateDialog
        open={!!creationMetadata?.isCreating}
        onOpenChange={(open) => {
          if (open) {
            handleCreateClick(creationMetadata?.initialTimestamp ?? new Date().getTime());
          } else {
            closeCreation();
          }
        }}
        onSuccess={closeCreation}
        timezone={timezone}
        initialFormValues={
          creationMetadata?.initialTimestamp
            ? {
                startDate: new Date(creationMetadata.initialTimestamp),
                duration: creationMetadata.initialDurationMinutes ?? 15,
                userIds: selectedUserIds.length ? selectedUserIds : undefined,
              }
            : undefined
        }
        showAvailabilityTab={!!creationMetadata?.isCreating}
      />
      <UnavailableUsersWarningDialog {...updateWarningDialogProps} />
    </section>
  );
};
